import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Badge from "@mui/material/Badge";
import Checkbox from "@mui/material/Checkbox";
import Button from "@mui/material/Button";
import FormControlLabel from "@mui/material/FormControlLabel";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {AvailabilityType} from "crqlar-react-common/dist/models/availability/availability.model";
import type {Resource} from "crqlar-react-common/dist/models/resource/resource.model";
import type {ResourceCategory} from "crqlar-react-common/dist/models/resourceCategory/resourceCategory.model";
import {endOfDay, startOfDay} from "crqlar-react-common/dist/utils/date.util";
import {useAvailabilityCalculationResults} from "crqlar-react-common/dist/utils/hooks/availability/useResourceAvailability";
import {useAllResourceCategoriesForSector} from "crqlar-react-common/dist/utils/hooks/resources/useAllResourceCategories";
import {useSector} from "crqlar-react-common/dist/utils/hooks/sector/useSectors";
import {getLocalizedValue} from "crqlar-react-common/dist/utils/translations.util";
import {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSectorIcon} from "../../../utils/reservation.util";
import {RightSidebarFloatingPanel} from "../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarPanelButton} from "../../layout/rightSidebar/RightSidebarRail";
import type {ReservationResourceSelection} from "./useReservationResourceSelection";

const AVAILABILITY_SYNC_KEY = "alwaysShowResourcesActiveOnDate";
const UNCATEGORIZED_GROUP_ID = "__uncategorized__";

type SortDirection = "asc" | "desc";

interface ResourceOption {
    id: string;
    label: string;
    resource: Resource;
}

interface ResourceGroup {
    id: string;
    label: string;
    resources: ResourceOption[];
}

interface ReservationResourcePickerProps {
    selection: ReservationResourceSelection;
    focusedDate: Date;
}

const getLabel = (
    item: Pick<Resource | ResourceCategory, "name" | "nameLocalized">,
    locale: string,
    fallback: string,
) => getLocalizedValue(locale, item.nameLocalized) || item.name || fallback;

const buildGroups = ({
    resources,
    categories,
    locale,
    sortDirection,
    unnamedLabel,
    uncategorizedLabel,
}: {
    resources: Resource[];
    categories: ResourceCategory[];
    locale: string;
    sortDirection: SortDirection;
    unnamedLabel: string;
    uncategorizedLabel: string;
}): ResourceGroup[] => {
    const collator = new Intl.Collator(locale, {numeric: true, sensitivity: "base"});
    const direction = sortDirection === "asc" ? 1 : -1;
    const options = resources
        .filter((resource): resource is Resource & {id: string} => Boolean(resource.id))
        .map(resource => ({
            id: resource.id,
            label: getLabel(resource, locale, unnamedLabel),
            resource,
        }))
        .sort((left, right) => direction * collator.compare(left.label, right.label));
    const groups = categories
        .filter((category): category is ResourceCategory & {id: string} => Boolean(category.id))
        .map(category => ({
            id: category.id,
            label: getLabel(category, locale, unnamedLabel),
            resources: options.filter(option => option.resource.categoryIds?.includes(category.id)),
        }))
        .filter(group => group.resources.length > 0)
        .sort((left, right) => direction * collator.compare(left.label, right.label));
    const groupedIds = new Set(groups.flatMap(group => group.resources.map(resource => resource.id)));
    const uncategorized = options.filter(option => !groupedIds.has(option.id));

    if (uncategorized.length) {
        groups.push({id: UNCATEGORIZED_GROUP_ID, label: uncategorizedLabel, resources: uncategorized});
    }

    return groups;
};

const getGroupSelection = (group: ResourceGroup, selectedIds: ReadonlySet<string>) => {
    const ids = Array.from(new Set(group.resources.map(resource => resource.id)));
    const selectedCount = ids.filter(id => selectedIds.has(id)).length;

    return {
        ids,
        all: ids.length > 0 && selectedCount === ids.length,
        some: selectedCount > 0 && selectedCount < ids.length,
    };
};

const rowSx = (selected: boolean) => ({
    m: 0,
    px: 0.75,
    py: 0.5,
    width: "100%",
    minWidth: 0,
    borderRadius: 1,
    bgcolor: selected ? "action.selected" : "transparent",
    "&:hover": {bgcolor: "action.hover"},
    "& .MuiFormControlLabel-label": {minWidth: 0},
});

export function ReservationResourcePicker({
    selection,
    focusedDate,
}: ReservationResourcePickerProps) {
    const {t, i18n} = useTranslation();
    const {
        sectorId,
        resources,
        selectedIds,
        selectedIdSet,
        allSelected,
        setSelectedIds,
        toggleAll,
        toggleGroup,
        toggleResource,
    } = selection;
    const sector = useSector(sectorId);
    const sectorIcon = useSectorIcon(sector);
    const categories = useAllResourceCategoriesForSector(sectorId);
    const [sortDirection, setSortDirection] = useState<SortDirection>("asc");
    const [sortAnchorEl, setSortAnchorEl] = useState<HTMLElement | null>(null);
    const [availabilitySyncEnabled, setAvailabilitySyncEnabled] = useState(
        () => localStorage.getItem(AVAILABILITY_SYNC_KEY) !== "false",
    );
    const locale = i18n.resolvedLanguage || i18n.language;
    const resourceIds = useMemo(
        () => resources.map(resource => resource.id).filter((id): id is string => Boolean(id)),
        [resources],
    );
    const availability = useAvailabilityCalculationResults({
        queryKey: ["availability", sectorId, resourceIds, focusedDate],
        entityIds: resourceIds.length ? resourceIds : undefined,
        dateRangeFrom: startOfDay(focusedDate),
        dateRangeTo: endOfDay(focusedDate),
    });
    const availableIds = useMemo(() => {
        const availableIdSet = new Set(
            (availability.data ?? [])
                .filter(result => result.type === AvailabilityType.OPEN || !result.type)
                .map(result => result.entityId as string),
        );

        return resourceIds.filter(id => availableIdSet.has(id));
    }, [availability.data, resourceIds]);
    const groupInput = useMemo(() => ({
        resources,
        categories,
        locale,
        sortDirection,
        unnamedLabel: t("unnamed"),
        uncategorizedLabel: t("Uncategorized"),
    }), [categories, locale, resources, sortDirection, t]);
    const groups = useMemo(
        () => buildGroups(groupInput),
        [groupInput],
    );
    const groupSelections = groups.length > 1
        ? groups.map(group => ({group, ...getGroupSelection(group, selectedIdSet)}))
        : [];
    const showGroupHeaders = groups.length > 1
        || groups.some(group => group.id !== UNCATEGORIZED_GROUP_ID);

    useEffect(() => {
        if (!availabilitySyncEnabled || !availability.isSuccess) return;
        setSelectedIds(availableIds);
    }, [availability.isSuccess, availabilitySyncEnabled, availableIds, setSelectedIds]);

    const setAvailabilitySync = (enabled: boolean) => {
        setAvailabilitySyncEnabled(enabled);
        localStorage.setItem(AVAILABILITY_SYNC_KEY, String(enabled));
    };

    const toggleAvailabilitySync = () => {
        const enabled = !availabilitySyncEnabled;
        setAvailabilitySync(enabled);

        if (!enabled) {
            setSelectedIds(resourceIds);
        }
    };

    const disableAvailabilitySync = () => {
        if (availabilitySyncEnabled) setAvailabilitySync(false);
    };

    const toggleAllResources = () => {
        if (availabilitySyncEnabled) {
            setAvailabilitySync(false);
            setSelectedIds(resourceIds);
            return;
        }

        toggleAll();
    };

    const renderQuickRow = (
        key: string,
        label: string,
        checked: boolean,
        indeterminate: boolean,
        onToggle: () => void,
        disabled = false,
    ) => (
        <FormControlLabel
            key={key}
            control={(
                <Checkbox
                    checked={checked}
                    indeterminate={indeterminate}
                    onChange={onToggle}
                    disabled={disabled}
                    size="small"
                    sx={{p: 0.5, mr: 0.5}}
                />
            )}
            label={<Typography noWrap>{label}</Typography>}
            sx={rowSx(checked || indeterminate)}
        />
    );

    const renderResourceLabel = (option: ResourceOption) => (
        <Stack minWidth={0}>
            <Typography noWrap>{option.label}</Typography>
            {(option.resource.meta?.isTemporary || option.resource.managedExternally) && (
                <Typography variant="caption" color="text.secondary" noWrap>
                    {[
                        option.resource.meta?.isTemporary ? t("temporary resource") : undefined,
                        option.resource.managedExternally ? t("Managed externally") : undefined,
                    ].filter(Boolean).join(" · ")}
                </Typography>
            )}
        </Stack>
    );

    return (
        <RightSidebarPanelButton
            icon={(
                <Badge
                    badgeContent={selectedIds.length}
                    showZero
                    invisible={resources.length === 0}
                >
                    {sectorIcon}
                </Badge>
            )}
            label={t("Resources")}
            disabled={resources.length === 0}
            renderPanel={close => (
                <RightSidebarFloatingPanel sx={{overflow: "hidden"}}>
                    <RightSidebarPanelHeader title={t("Resources")} onClose={close}/>

                    <Stack sx={{px: 2, pb: 1.5, gap: 1.25, flexShrink: 0}}>
                        <Typography variant="body2" fontWeight={700} sx={{px: 0.75, py: 0.5}}>
                            {t("Filter by resources")}
                        </Typography>

                        <Stack gap={0.25}>
                            {renderQuickRow(
                                "working-today",
                                t("Working today"),
                                availabilitySyncEnabled,
                                false,
                                toggleAvailabilitySync,
                                !availabilitySyncEnabled && (availability.isLoading || availability.isError),
                            )}
                            {renderQuickRow(
                                "all",
                                t("All resources"),
                                !availabilitySyncEnabled && allSelected,
                                false,
                                toggleAllResources,
                            )}
                            {groupSelections.map(({group, ids, all, some}) => renderQuickRow(
                                `group-${group.id}`,
                                group.label,
                                all,
                                some,
                                () => {
                                    disableAvailabilitySync();
                                    toggleGroup(ids);
                                },
                            ))}
                        </Stack>

                        {availability.isError && (
                            <Typography variant="caption" color="error">
                                {t("Something went wrong")}
                            </Typography>
                        )}

                    </Stack>

                    <Stack sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2}}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            sx={{py: 1, flexShrink: 0}}
                        >
                            <Typography variant="body2" fontWeight={700} sx={{px: 0.75, py: 0.5}}>
                                {t("All resources")}
                            </Typography>
                            <Button
                                type="button"
                                size="small"
                                color="info"
                                endIcon={<ArrowDropDownIcon/>}
                                onClick={event => setSortAnchorEl(event.currentTarget)}
                                sx={{height: 36, minWidth: 0, whiteSpace: "nowrap"}}
                            >
                                {sortDirection === "asc" ? "A–Z" : "Z–A"}
                            </Button>
                            <Menu
                                anchorEl={sortAnchorEl}
                                open={Boolean(sortAnchorEl)}
                                onClose={() => setSortAnchorEl(null)}
                                anchorOrigin={{vertical: "bottom", horizontal: "right"}}
                                transformOrigin={{vertical: "top", horizontal: "right"}}
                            >
                                <MenuItem
                                    selected={sortDirection === "asc"}
                                    onClick={() => {
                                        setSortDirection("asc");
                                        setSortAnchorEl(null);
                                    }}
                                >
                                    A–Z
                                </MenuItem>
                                <MenuItem
                                    selected={sortDirection === "desc"}
                                    onClick={() => {
                                        setSortDirection("desc");
                                        setSortAnchorEl(null);
                                    }}
                                >
                                    Z–A
                                </MenuItem>
                            </Menu>
                        </Stack>

                        {groups.length === 0 ? (
                            <Typography color="text.secondary" sx={{py: 2}}>
                                {t("No resources")}
                            </Typography>
                        ) : (
                            <Stack gap={1.25}>
                                {groups.map(group => (
                                    <Stack key={group.id} gap={0.25}>
                                        {showGroupHeaders && (
                                            <Typography variant="body2" fontWeight={700} sx={{px: 0.75, py: 0.5}}>
                                                {group.label}
                                            </Typography>
                                        )}
                                        {group.resources.map(option => {
                                            const selected = selectedIdSet.has(option.id);

                                            return (
                                                <FormControlLabel
                                                    key={`${group.id}-${option.id}`}
                                                    control={(
                                                        <Checkbox
                                                            checked={selected}
                                                            onChange={() => {
                                                                disableAvailabilitySync();
                                                                toggleResource(option.id);
                                                            }}
                                                            size="small"
                                                            sx={{p: 0.5, mr: 0.5}}
                                                        />
                                                    )}
                                                    label={renderResourceLabel(option)}
                                                    sx={rowSx(selected)}
                                                />
                                            );
                                        })}
                                    </Stack>
                                ))}
                            </Stack>
                        )}
                    </Stack>
                </RightSidebarFloatingPanel>
            )}
        />
    );
}
